/**
 * FAQ page behaviour: client-side question filtering, a Quick-links rail
 * that tracks which group is in view, smooth-scroll to that group, and a
 * height animation on each question — the same two motion jobs the live
 * theme did in `theme.Faq` / `AccordionInstance`.
 *
 * The rail is rendered by the section from its own blocks, so this only
 * has to filter, highlight, scroll, and animate.
 */
if (!customElements.get('faq-page')) {
  class FaqPage extends HTMLElement {
    observer = undefined;
    isScrolling = false;
    scrollFrame = 0;

    /** Quick-links scroll duration in ms. Higher = slower. Live theme was 700. */
    static SCROLL_DURATION_MS = 900;

    /**
     * Filters question rows against the query, then hides any group left with
     * no visible rows.
     * @param {Event} event
     */
    onSearchInput = (event) => {
      const query = event.target.value.trim().toLowerCase();
      let matches = 0;

      for (const group of this.groups) {
        let groupMatches = 0;

        for (const item of group.querySelectorAll('[data-faq-item]')) {
          const question = item.querySelector('[data-faq-question]')?.textContent.toLowerCase() ?? '';
          const isMatch = query === '' || question.includes(query);

          item.hidden = !isMatch;

          if (isMatch) {
            groupMatches += 1;
          }

          // Collapse rows as they are filtered so the list does not jump.
          if (query !== '') {
            this.collapseItem(item);
          }
        }

        group.hidden = groupMatches === 0;
        matches += groupMatches;
      }

      if (this.noResults) {
        this.noResults.hidden = query === '' || matches > 0;
      }

      this.syncSidebarToVisibleGroups();
    };

    /**
     * Smooth-scrolls the matching group into view, the way the live theme's
     * `theme.viewport.scroll.to` did on Quick-links clicks.
     * @param {MouseEvent} event
     */
    onSidebarClick = (event) => {
      const link = event.target.closest('.faq__sidebar-link');
      if (!link || !this.contains(link)) {
        return;
      }

      const group = this.groupForLink(link);
      if (!group || group.hidden) {
        return;
      }

      event.preventDefault();
      this.setActiveLink(link);
      this.scrollToGroup(group);

      const id = group.id;
      if (id && history.replaceState) {
        history.replaceState(null, '', `#${id}`);
      }
    };

    /**
     * Animates a question open or closed instead of letting native <details>
     * snap. Keyboard activation still fires click on <summary>.
     * @param {MouseEvent} event
     */
    onItemClick = (event) => {
      const toggle = event.target.closest('.faq__question');
      if (!toggle || !this.contains(toggle)) {
        return;
      }

      const item = toggle.closest('[data-faq-item]');
      const panel = item?.querySelector('.faq__panel');
      if (!item || !panel) {
        return;
      }

      event.preventDefault();

      if (this.prefersReducedMotion) {
        const willOpen = !item.open;
        this.collapseItem(item);
        item.open = willOpen;
        item.classList.toggle('is-open', willOpen);
        return;
      }

      if (item.open) {
        this.closeItem(item, panel);
      } else {
        this.openItem(item, panel);
      }
    };

    /**
     * @param {TransitionEvent} event
     */
    onPanelTransitionEnd = (event) => {
      if (!event.target.classList.contains('faq__panel') || event.propertyName !== 'height') {
        return;
      }

      const panel = event.target;
      const item = panel.closest('[data-faq-item]');
      if (!item) {
        return;
      }

      if (item.classList.contains('is-closing')) {
        item.classList.remove('is-closing');
        item.open = false;
      }

      panel.getAttribute('style');
      panel.removeAttribute('style');
    };

    connectedCallback() {
      for (const item of this.querySelectorAll('[data-faq-item][open]')) {
        item.classList.add('is-open');
      }

      this.classList.add('faq--animated');
      this.searchInput?.addEventListener('input', this.onSearchInput);
      this.addEventListener('click', this.onSidebarClick);
      this.addEventListener('click', this.onItemClick);
      this.addEventListener('transitionend', this.onPanelTransitionEnd);
      this.observeGroups();
    }

    disconnectedCallback() {
      this.searchInput?.removeEventListener('input', this.onSearchInput);
      this.removeEventListener('click', this.onSidebarClick);
      this.removeEventListener('click', this.onItemClick);
      this.removeEventListener('transitionend', this.onPanelTransitionEnd);
      this.observer?.disconnect();
      this.cancelScroll();
    }

    get searchInput() {
      return this.querySelector('[data-faq-search]');
    }

    get noResults() {
      return this.querySelector('[data-faq-no-results]');
    }

    get groups() {
      return [...this.querySelectorAll('[data-faq-group]')];
    }

    get sidebarLinks() {
      return [...this.querySelectorAll('.faq__sidebar-link')];
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /** Greys out rail links whose group is filtered out of view. */
    syncSidebarToVisibleGroups() {
      for (const link of this.sidebarLinks) {
        const group = this.groupForLink(link);
        if (!group) {
          continue;
        }

        link.toggleAttribute('hidden', group.hidden);
      }
    }

    /**
     * @param {HTMLAnchorElement} link
     * @returns {HTMLElement | null}
     */
    groupForLink(link) {
      const id = link.getAttribute('href')?.slice(1);
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }

    /**
     * Marks the rail link for whichever group is nearest the top of the
     * viewport. The live theme did this on a scroll handler; an observer is
     * cheaper and needs no throttling. Clicks set `isScrolling` so the
     * observer does not steal the highlight mid-animation.
     */
    observeGroups() {
      if (!this.sidebarLinks.length || !this.groups.length) {
        return;
      }

      this.setActiveLink(this.sidebarLinks[0]);

      this.observer = new IntersectionObserver(
        (entries) => {
          if (this.isScrolling) {
            return;
          }

          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((first, second) => first.boundingClientRect.top - second.boundingClientRect.top);

          if (!visible.length) {
            return;
          }

          const link = this.sidebarLinks.find((candidate) => candidate.getAttribute('href') === `#${visible[0].target.id}`);

          if (link) {
            this.setActiveLink(link);
          }
        },
        { rootMargin: '-10% 0px -70% 0px', threshold: 0 },
      );

      for (const group of this.groups) {
        this.observer.observe(group);
      }
    }

    /** @param {HTMLAnchorElement} activeLink */
    setActiveLink(activeLink) {
      for (const link of this.sidebarLinks) {
        const isActive = link === activeLink;
        link.classList.toggle('faq__sidebar-link--active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      }
    }

    /**
     * Extra 50px matches the live theme's `scroll.to(..., -1, 50)`. Sticky
     * header height is added when the bar is currently overlapping the top
     * of the viewport.
     * @returns {number}
     */
    scrollPadding() {
      const extra = 50;
      const header = document.querySelector('.lp-index-header');
      if (!header) {
        return extra;
      }

      const rect = header.getBoundingClientRect();
      if (rect.height && rect.top <= extra && rect.bottom > 0) {
        return rect.bottom + extra;
      }

      return extra;
    }

    /**
     * @param {HTMLElement} group
     */
    scrollToGroup(group) {
      const targetY = this.groupScrollTop(group);

      if (this.prefersReducedMotion) {
        this.cancelScroll();
        window.scrollTo(0, targetY);
        return;
      }

      this.isScrolling = true;
      this.animateScroll(targetY, FaqPage.SCROLL_DURATION_MS, () => {
        this.isScrolling = false;
      });
    }

    /**
     * @param {HTMLElement} group
     * @returns {number}
     */
    groupScrollTop(group) {
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = window.scrollY + group.getBoundingClientRect().top - this.scrollPadding();
      return Math.min(Math.max(0, y), maxY);
    }

    /**
     * jQuery `swing` easing — same curve as `theme.viewport.scroll.to`
     * on the live FAQ rail. Duration comes from `SCROLL_DURATION_MS`.
     * @param {number} targetY
     * @param {number} duration
     * @param {() => void} [done]
     */
    animateScroll(targetY, duration, done) {
      this.cancelScroll(false);

      const startY = window.scrollY;
      const delta = targetY - startY;
      const start = performance.now();

      const step = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 0.5 - Math.cos(t * Math.PI) / 2;
        window.scrollTo(0, startY + delta * eased);

        if (t < 1) {
          this.scrollFrame = requestAnimationFrame(step);
          return;
        }

        this.scrollFrame = 0;
        done?.();
      };

      this.scrollFrame = requestAnimationFrame(step);
    }

    /**
     * @param {boolean} [clearFlag]
     */
    cancelScroll(clearFlag = true) {
      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = 0;
      }

      if (clearFlag) {
        this.isScrolling = false;
      }
    }

    /**
     * @param {HTMLDetailsElement} item
     * @param {HTMLElement} panel
     */
    openItem(item, panel) {
      panel.style.height = '0px';
      item.open = true;
      panel.getBoundingClientRect();
      panel.style.height = `${panel.scrollHeight}px`;

      window.setTimeout(() => {
        item.classList.add('is-open');
      }, 10);
    }

    /**
     * @param {HTMLDetailsElement} item
     * @param {HTMLElement} panel
     */
    closeItem(item, panel) {
      panel.style.height = `${panel.scrollHeight}px`;
      item.classList.remove('is-open');
      item.classList.add('is-closing');

      window.setTimeout(() => {
        panel.style.height = '0px';
      }, 10);
    }

    /** Instantly shut a row — used by search so filtered lists do not jump. */
    collapseItem(item) {
      item.open = false;
      item.classList.remove('is-open', 'is-closing');
      item.querySelector('.faq__panel')?.removeAttribute('style');
    }
  }

  customElements.define('faq-page', FaqPage);
}
